import { useMemo, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { clampToMax, clampToRange } from "./indicatorMath";

/** Hero-size bounds from the Almikia design guide (section 3, "El círculo, técnico"). */
const MIN_SIZE = 120;
const MAX_SIZE = 250;
const DEFAULT_SIZE = 120;

/** Stroke thickness must stay within 8%-10% of the ring's diameter. */
const MIN_STROKE_RATIO = 0.08;
const MAX_STROKE_RATIO = 0.1;
const DEFAULT_STROKE_RATIO = 0.09;

/**
 * Angle-anchored color stops (magenta -> orange -> yellow -> green), fixed
 * along the circle regardless of the swept value, per the guide.
 */
const RING_GRADIENT =
  "conic-gradient(from 0deg, #B42E77 0%, #D14E2F 38%, #F1D432 70%, #23794A 100%)";

export type ScoreRingSurface = "light" | "navy";

export interface ScoreRingProps {
  /** Raw value to display; clamped to [0, max]. Non-finite values render as 0. */
  value: number;
  /** Upper bound of the value range. Defaults to 100. */
  max?: number;
  /** Diameter in px. Clamped to the hero range [120, 250]. Defaults to 120. */
  size?: number;
  /** Stroke thickness override in px; clamped to 8%-10% of the (clamped) size. */
  strokeWidth?: number;
  /** Small caller-supplied unit shown next to the value (e.g. "/ 100", "de 8"). */
  unit?: string;
  /** Accessible name for the ring (e.g. "Puntaje de calidad del expediente"). */
  label: string;
  /** Which surface the ring sits on, to pick the correct track color. Defaults to "light". */
  surface?: ScoreRingSurface;
  className?: string;
}

/**
 * The gradient "hero" circle from the Almikia indicator grammar: a fixed
 * magenta -> orange -> yellow -> green sweep anchored to angle (not to the
 * arc length), so a low score reads magenta/orange and a full ring reads
 * green all the way around. Built from two conic-gradient layers (a color
 * sweep and an angle mask, intersected with a radial mask that punches the
 * ring's hollow center) rather than an SVG stroke-dasharray or a rotated
 * linearGradient: a diagonal linearGradient on a rotated circle does not
 * keep colors anchored to the swept arc, and a circle's start/end point are
 * the same point, so a naive gradient can miscolor both low and full values.
 */
export function ScoreRing({
  value,
  max = 100,
  size = DEFAULT_SIZE,
  strokeWidth,
  unit,
  label,
  surface = "light",
  className,
}: ScoreRingProps) {
  const effectiveSize = clampToRange(size, MIN_SIZE, MAX_SIZE);

  const stroke = useMemo(() => {
    const minStroke = effectiveSize * MIN_STROKE_RATIO;
    const maxStroke = effectiveSize * MAX_STROKE_RATIO;
    const requested = strokeWidth ?? effectiveSize * DEFAULT_STROKE_RATIO;
    return clampToRange(requested, minStroke, maxStroke);
  }, [effectiveSize, strokeWidth]);

  const { clamped, fraction } = clampToMax(value, max);
  const angleDeg = fraction * 360;

  const outerRadius = effectiveSize / 2;
  const innerRadius = outerRadius - stroke;

  const trackColor = surface === "navy" ? "#3B4170" : "#EEE9E1";

  const ringBandMask = `radial-gradient(circle, transparent 0, transparent ${innerRadius}px, black ${innerRadius}px, black ${outerRadius}px, transparent ${outerRadius}px, transparent 100%)`;

  const angleMaskStyle = {
    "--score-ring-angle": `${angleDeg}deg`,
    WebkitMaskImage: `conic-gradient(from 0deg, black 0deg, black var(--score-ring-angle), transparent var(--score-ring-angle), transparent 360deg), ${ringBandMask}`,
    maskImage: `conic-gradient(from 0deg, black 0deg, black var(--score-ring-angle), transparent var(--score-ring-angle), transparent 360deg), ${ringBandMask}`,
    WebkitMaskComposite: "source-in",
    maskComposite: "intersect",
    backgroundImage: RING_GRADIENT,
  } as unknown as CSSProperties;

  const trackMaskStyle: CSSProperties = {
    WebkitMaskImage: ringBandMask,
    maskImage: ringBandMask,
    backgroundColor: trackColor,
  };

  const displayValue = Number.isInteger(clamped)
    ? clamped
    : Math.round(clamped * 10) / 10;

  return (
    <div
      role="meter"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
      className={cn(
        "relative inline-flex items-center justify-center",
        className,
      )}
      style={{ width: effectiveSize, height: effectiveSize }}
      data-testid="score-ring"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 rounded-full"
        style={trackMaskStyle}
      />
      <div
        aria-hidden="true"
        data-testid="score-ring-arc"
        className="absolute inset-0 rounded-full"
        style={angleMaskStyle}
      />
      <div
        aria-hidden="true"
        className="relative flex flex-col items-center justify-center leading-none"
      >
        <span
          className="font-bold tabular-nums"
          style={{ fontSize: Math.max(effectiveSize * 0.24, 18) }}
        >
          {displayValue}
        </span>
        {unit ? (
          <span
            className="mt-1 text-[12.5px] tabular-nums"
            style={{ opacity: 0.8 }}
          >
            {unit}
          </span>
        ) : null}
      </div>
    </div>
  );
}
